import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

// UseCase
import {
  createValidationSchema,
  getDefaultSortOrder,
  // formatInitialSorting,
} from './tableSortByUseCase';

/**
 * @typedef {object} useTableSortByReturn
 * @property {Record<string, ApiManagerTableFiltersSortByCriteria>} filtersById
 * @property {import('react-hook-form').UseFormReturn<{ filters: ApiManagerTableFiltersSortByCriteriaValue[] }>} formFilters
 * @property {Array<ApiManagerTableFiltersSortByCriteriaValue & {_id: string}>} filtersFields
 * @property {() => void} handleAddFilter
 * @property {(index: number) => void} handleRemoveFilter
 * @property {() => void} handleClearAllSortByFilters
 * @property {(index: number, next: ApiManagerTableFiltersSortByCriteriaValue) => void} handleUpdateTypeFilter
 * @property {(index: number, next: ApiManagerTableFiltersSortByCriteriaValue) => void} handleUpdateOrderByFilter
 * @property {(items: Array<ApiManagerTableFiltersSortByCriteriaValue & {_id: string}>, oldIndex: number, newIndex: number) => void} handleFiltersOrder
 * @property {() => void} handleCancelFilters
 * @property {(fieldsArray: ApiManagerTableFiltersSortByCriteriaValue[]) => number} getFiltersCount
 * @property {boolean} canAddFilter
 * @property {boolean} popoverSortByIsOpen
 * @property {(isOpen: boolean) => void} setPopoverSortByIsOpen
 */

/**
 * Hook to manage the table sort by popover
 * @param {object} props
 * @param {ApiManagerTableFiltersSortBy['criteria']} props.tableFilters
 * @param {ApiManagerTableFiltersSortBy['criteriaValues']} [props.initialValuesFilters]
 * @param {boolean} props.isLoading
 * @param {boolean} props.isMultiCriteria - Whether the sort by can have multiple criteria
 * @returns {useTableSortByReturn} The table sort by state and actions
 */
const useTableSortBy = ({
  tableFilters, initialValuesFilters, isLoading, isMultiCriteria,
}) => {
  const [popoverSortByIsOpen, setPopoverSortByIsOpen] = useState(false);
  // const [tableFilterSorting, setTableFilterSorting] = useState([]);

  // /**
  //  * UseEffect to format the initial sorting
  //  */
  // useEffect(() => {
  //   const formattedSorting = formatInitialSorting(initialValuesSorting);
  //   setTableFilterSorting(formattedSorting);
  // }, [initialValuesSorting]);

  // Form for conditional filters
  /** @type {ApiManagerTableFiltersSortByCriteriaValue} */
  const filtersInitial = {
    id: '',
    order: '',
  };
  /** @type {import('react-hook-form').UseFormReturn<{ filters: ApiManagerTableFiltersSortByCriteriaValue[] }>} */
  const formFilters = useForm({
    defaultValues: {
      filters: initialValuesFilters && initialValuesFilters.length > 0 ? initialValuesFilters : [filtersInitial],
    },
    resolver: zodResolver(createValidationSchema()),
    mode: 'onChange',
  });

  /**
   * UseEffect to reset the form with the initial values
   */
  useEffect(() => {
    if (initialValuesFilters && initialValuesFilters.length > 0 && !isLoading) {
      formFilters.reset({ filters: initialValuesFilters });
    }
  }, [initialValuesFilters, isLoading, formFilters]);

  const { watch, control, reset } = formFilters;
  const watchedFilters = watch('filters');

  const {
    fields /** @type {ApiManagerTableFiltersSortByCriteriaValue[]} */,
    append,
    remove,
    update,
    move,
  } = useFieldArray({
    control,
    name: 'filters',
    keyName: '_id',
    rules: {
      maxLength: isMultiCriteria ? 1 : undefined, // If not multi criteria, only one filter is allowed
    },
  });

  const filtersById = useMemo(() => (isLoading || !tableFilters ? {} : tableFilters.reduce(
    (acc, filter) => {
      acc[filter.id] = filter;
      return acc;
    },
    /** @type {Record<string, ApiManagerTableFiltersSortByCriteria>} */ ({}),
  )), [tableFilters, isLoading]);

  /**
   * Handle add filter
   */
  const handleAddFilter = () => {
    if (!isMultiCriteria && watchedFilters.length >= 1) return;
    if (watchedFilters.length >= 10) return;
    // If the first filter is empty, reset the form with the initial values
    if (watchedFilters?.length && !watchedFilters[0].id) {
      update(0, filtersInitial);
    }
    append(filtersInitial);
  };

  /**
   * Handle clear all filters
   */
  const handleClearAllFilters = () => {
    reset({ filters: [{}] });
  };

  /**
   * Handle remove filter
   * @param {number} index
   */
  const handleRemoveFilter = (index) => {
    remove(index);
    if (watchedFilters.length <= 1) {
      handleClearAllFilters();
    }
  };

  /**
   * Handle clear all filters
   */
  const handleClearAllSortByFilters = () => {
    handleClearAllFilters();
  };

  /**
   * Handle update filter
   * @param {number} index
   * @param {ApiManagerTableFiltersSortByCriteriaValue} next
   */
  const handleUpdateTypeFilter = (index, next) => {
    const field = watchedFilters[index];
    if (next.id === field.id) return;
    const newValue = {
      id: next.id,
      order: next.order || getDefaultSortOrder(filtersById[next.id]),
    };
    update(index, newValue);
  };

  /**
   * Handle update order by filter
   * @param {number} index
   * @param {ApiManagerTableFiltersSortByCriteriaValue} next
   */
  const handleUpdateOrderByFilter = (index, next) => {
    const field = watchedFilters[index];
    if (next.order === field.order) return;
    const newValue = {
      ...field,
      id: next.id,
      order: next.order,
    };
    update(index, newValue);
  };

  /**
   * Handle move filter
   * @param {Array<ApiManagerTableFiltersSortByCriteriaValue & {_id: string}>} _items
   * @param {number} oldIndex
   * @param {number} newIndex
   */
  const handleFiltersOrder = (_items, oldIndex, newIndex) => {
    move(oldIndex, newIndex);
  };

  /**
   * Handle cancel filters
   */
  const handleCancelFilters = () => {
    formFilters.reset({ filters: initialValuesFilters });
    setPopoverSortByIsOpen(false);
  };

  // /**
  //  * Handle set table filter sorting
  //  * @param {import('@tanstack/react-table').SortingState} sorting
  //  */
  // const handleSetTableFilterSorting = (sorting) => {
  //   if (initialValuesFilters?.length && initialValuesFilters[0].id && sorting.length) return;
  //   setTableFilterSorting(sorting);
  // };

  /**
   * Get the number of conditional filters
   * @param {ApiManagerTableFiltersSortByCriteriaValue[]} fieldsArray
   * @returns {number}
   */
  const getFiltersCount = useCallback((/** @type {ApiManagerTableFiltersSortByCriteriaValue[]} */ fieldsArray) => (
    fieldsArray?.length ? fieldsArray.filter(
      (/** @type {ApiManagerTableFiltersSortByCriteriaValue} */ filter) => !!filter.id,
    ).length : 0), []);

  // /**
  //  * Get the formatted sorting for the table filter
  //  * @returns {ApiManagerTableFiltersSortByCriteriaValue | null}
  //  */
  // const tableFilterSortingFormatted = useMemo(() => {
  //   const activeSorting = tableFilterSorting?.length ? tableFilterSorting?.[0] : null;
  //   if (!activeSorting) return null;

  //   return {
  //     type: activeSorting?.id,
  //     orderBy: activeSorting?.desc ? 'desc' : 'asc',
  //     order: 0,
  //   };
  // }, [tableFilterSorting]);

  /**
   * Check if the user can add a filter
   * @returns {boolean}
   */
  const canAddFilter = useMemo(() => (isMultiCriteria ? watchedFilters.length < 10 : watchedFilters.length < 1), [watchedFilters, isMultiCriteria]);

  return {
    filtersById,
    formFilters,
    filtersFields: fields,
    handleAddFilter,
    handleRemoveFilter,
    handleClearAllSortByFilters,
    handleUpdateTypeFilter,
    handleUpdateOrderByFilter,
    handleFiltersOrder,
    handleCancelFilters,

    popoverSortByIsOpen,
    setPopoverSortByIsOpen,

    getFiltersCount,
    canAddFilter,

    // Sorting
    // tableFilterSortingFormatted,
    // tableFilterSorting,
    // handleSetTableFilterSorting,
  };
};

export default useTableSortBy;
